import { useEffect, useState } from "react";
import { ApiError, PayrollValidationError, calculatePayrollRun } from "@/api/client";
import type { PayrollEmployee } from "@/api/types";
import { PAY_PERIODS_PER_YEAR, TAX_YEAR } from "@/pages/payroll-fixtures";

export type H2aExemptionStatus = "idle" | "loading" | "ready" | "error";

export interface UseH2aExemptionResult {
  /**
   * Employee-side Social Security + Medicare that WOULD be withheld from the H-2A
   * roster this pay period if the exemption did not apply.
   *
   * This is the engine's own `totals.ficaMedicare` from a second run — not a
   * difference computed here, and not a rate applied to a wage figure on the client.
   * The real run already returns $0.00 for these people; this is the other side of
   * that fact, which is the only form in which the exemption has a dollar value at all.
   */
  wouldBeWithheld: number | null;
  status: H2aExemptionStatus;
  error: string | null;
}

/**
 * What the H-2A exemption is worth, asked of the engine rather than derived.
 *
 * The Payroll screen shows H-2A workers with $0.00 FICA, which is correct and also
 * says nothing: a reader cannot tell a statutory exemption worth real money from a
 * line that happens to be zero. The number that carries the point — "this is what
 * would come out of their checks otherwise" — does not exist anywhere in the normal
 * run, because the normal run is the exempt one.
 *
 * So it is obtained the only honest way: POST /api/payroll/run a second time, over
 * the H-2A subset only, with `ficaExempt: false`. Every figure this hook returns is
 * a server-computed total from that response. NOTHING here multiplies a wage by a
 * rate, and nothing subtracts one run from another — the counterfactual run's own
 * total IS the answer, since the real run's is zero by construction.
 *
 * <p>
 * EMPLOYER MATCH IS DELIBERATELY NOT REPORTED. `PaycheckResponse` carries
 * `socialSecurityEmployer`/`medicareEmployer` per employee, but `PayrollRunTotals`
 * does not total them, and summing them here would put payroll arithmetic in the
 * frontend — the exact thing usePayrollRun's totals note refuses. The employee-side
 * figure makes the point without inventing a second implementation of a total.
 * </p>
 *
 * @param h2aEmployees The H-2A subset of the roster, or `null` while the roster is
 * unknown. `null` and `[]` differ for the same reason they differ in usePayrollRun:
 * `[]` is a real answer (no H-2A workers on this payroll, so the exemption is worth
 * nothing here) and reports `ready` with `wouldBeWithheld` of `0`, while `null` stays
 * idle and asks the engine nothing.
 */
export function useH2aExemption(h2aEmployees: PayrollEmployee[] | null): UseH2aExemptionResult {
  const [wouldBeWithheld, setWouldBeWithheld] = useState<number | null>(null);
  const [status, setStatus] = useState<H2aExemptionStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (h2aEmployees === null) {
      setStatus("idle");
      setWouldBeWithheld(null);
      setError(null);
      return;
    }

    // A farm with no H-2A workers is a real, complete answer — not a request to make.
    // The engine rejects an empty roster at the request level (400), so asking would
    // turn "nobody is exempt here" into an error message about a malformed run.
    if (h2aEmployees.length === 0) {
      setStatus("ready");
      setWouldBeWithheld(0);
      setError(null);
      return;
    }

    let cancelled = false;
    const controller = new AbortController();
    setStatus("loading");
    setError(null);

    calculatePayrollRun(
      {
        employees: h2aEmployees.map((employee) => ({
          employeeId: employee.id,
          grossWages: employee.grossWages,
          payPeriodsPerYear: PAY_PERIODS_PER_YEAR,
          taxYear: TAX_YEAR,
          filingStatus: "Single",
          // The whole point of this call: the SAME people, run as if the exemption
          // did not exist. Everything else matches usePayrollRun exactly, so the
          // only difference between the two answers is the one being measured.
          ficaExempt: false,
        })),
      },
      controller.signal,
    )
      .then((run) => {
        if (cancelled) return;
        setWouldBeWithheld(run.totals.ficaMedicare);
        setStatus("ready");
      })
      .catch((cause: unknown) => {
        if (cancelled || (cause instanceof DOMException && cause.name === "AbortError")) return;
        // Reported, never degraded to zero. "$0.00" is what the exemption legitimately
        // makes the REAL run say, so a failed counterfactual that fell back to zero
        // would be indistinguishable from a farm where the exemption saves nothing.
        setWouldBeWithheld(null);
        setStatus("error");
        setError(
          cause instanceof PayrollValidationError
            ? Object.values(cause.errors).flat().join(" ")
            : cause instanceof ApiError
              ? cause.message
              : "Could not reach the payroll engine.",
        );
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [h2aEmployees]);

  return { wouldBeWithheld, status, error };
}
